/**
 * Rooms Service — NC-797 (Schulungsraum R2 Admin-CRUD).
 *
 * Admin-only write surface (POST / PATCH / DELETE) + viewer-accessible read
 * surface (GET list / GET by id).  Reuses the shared apiClient so 401/403/5xx
 * flow through the existing global error interceptor.
 */

import apiClient from './api';
import { ApiResponse, SchulungsraumDefinition } from './types';

export interface RoomsListParams {
  /** When defined, filters by isActive. When omitted, returns all rooms. */
  isActive?: boolean;
}

export type RoomCreateInput = Omit<
  SchulungsraumDefinition,
  'id' | 'pk' | 'createdAt' | 'createdBy' | 'modifiedAt' | 'modifiedBy'
>;

export type RoomUpdateInput = Partial<
  Omit<SchulungsraumDefinition, 'id' | 'pk' | 'createdAt' | 'createdBy' | 'modifiedAt' | 'modifiedBy'>
>;

export const roomsApi = {
  /**
   * List all rooms.  Pass `{ isActive: true }` to restrict to active rooms
   * (e.g. for the run-create dropdown in R3), or omit to get all (admin
   * CRUD view needs inactive rows too).
   */
  async list(params: RoomsListParams = {}): Promise<SchulungsraumDefinition[]> {
    const queryParams: Record<string, string> = {};
    if (params.isActive !== undefined) {
      queryParams.isActive = String(params.isActive);
    }
    const response = await apiClient.get<ApiResponse<SchulungsraumDefinition[]>>('/rooms', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch rooms');
    }
    return response.data.data;
  },

  /**
   * Fetch a single room by ID.
   */
  async getById(id: string): Promise<SchulungsraumDefinition> {
    const response = await apiClient.get<ApiResponse<SchulungsraumDefinition>>(
      `/rooms/${encodeURIComponent(id)}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch room');
    }
    return response.data.data;
  },

  /**
   * Admin-only: create a new room.
   */
  async create(input: RoomCreateInput): Promise<SchulungsraumDefinition> {
    const response = await apiClient.post<ApiResponse<SchulungsraumDefinition>>('/rooms', input);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create room');
    }
    return response.data.data;
  },

  /**
   * Admin-only: update an existing room (partial PATCH).
   * Used for both field edits and isActive toggling (reactivation).
   */
  async update(id: string, updates: RoomUpdateInput): Promise<SchulungsraumDefinition> {
    const response = await apiClient.patch<ApiResponse<SchulungsraumDefinition>>(
      `/rooms/${encodeURIComponent(id)}`,
      updates
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update room');
    }
    return response.data.data;
  },

  /**
   * Admin-only: soft-delete (deactivate) a room.
   * The BE sets isActive=false — use update({ isActive: true }) to reactivate.
   */
  async delete(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `/rooms/${encodeURIComponent(id)}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to deactivate room');
    }
  },
};
